import { SplitActions, SplitTypes } from 'domains/campaigns/types';

type ExitFixableSend = {
    id?: number;
    parentId?: number;
    parentIndex?: number;
    splitType?: SplitTypes;
    splitAction?: SplitActions;
};

/**
 * Exit-curation sends are virtual (no DB row) so the backend never serialises `id` or `parentId`
 * for them. Without a `parentId` they appear as graph roots and the `undefined === undefined`
 * comparison in parent-matching logic makes them falsely match each other.
 *
 * Primary fix: the published structure always carries `parentIndex` on exit sends, pointing
 * directly to the parent send's array position. Use it to look up the parent's id. This handles
 * all exit shapes: MEETS exits, DNM exits, both-branches-exit splits, and exits serialized
 * before their MEETS sibling.
 *
 * Fallback: for DNM exits that lack `parentIndex`, scan backwards to the nearest preceding MEETS
 * send with a `parentId`. This preserves compatibility for callers that supply exits without the
 * `parentIndex` field (e.g. unit-test fixtures).
 *
 * Generic over T so it preserves the concrete `Send` subtype in callers that need it.
 */
const inferExitSendParentIds = <T extends ExitFixableSend>(sends: T[]): T[] =>
    sends.map((send, index, arr) => {
        if (send.id == null && send.parentId == null && send.splitAction === SplitActions.EXIT) {
            if (send.parentIndex != null) {
                const parentId = arr[send.parentIndex]?.id;
                if (parentId != null) {
                    return { ...send, parentId };
                }
            }
            if (send.splitType === SplitTypes.DOES_NOT_MEET) {
                for (let i = index - 1; i >= 0; i--) {
                    const prev = arr[i];
                    if (prev.parentId != null && prev.splitType === SplitTypes.MEETS) {
                        return { ...send, parentId: prev.parentId };
                    }
                }
            }
        }
        return send;
    });

export default inferExitSendParentIds;
